import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { listCourses, type CourseListItem } from "../api/courses";
import { listUsers, type UserListItem } from "../api/users";
import {
  scheduleExam,
  type ExamFormat,
} from "../api/examSchedules";
import { ApiError } from "../api/client";
import { moscowDateTimeToUtcIso } from "../utils/datetime";

interface Props {
  traineeUserId: number;
  traineeName: string;
  onClose: () => void;
  onScheduled: () => void;
}

export function ScheduleExamDialog({
  traineeUserId,
  traineeName,
  onClose,
  onScheduled,
}: Props) {
  const [courses, setCourses] = useState<CourseListItem[]>([]);
  const [examiners, setExaminers] = useState<UserListItem[]>([]);
  const [loading, setLoading] = useState(true);

  const [courseId, setCourseId] = useState<number | "">("");
  const [examinerId, setExaminerId] = useState<number | "">("");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("11:00");
  const [format, setFormat] = useState<ExamFormat>("in_person");
  const [comment, setComment] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const [cr, us] = await Promise.all([
          listCourses({ page_size: 100 }),
          listUsers({ status: "active", page_size: 100 }),
        ]);
        const usable = cr.items.filter((c) => c.status !== "archived");
        setCourses(usable);
        if (usable.length > 0) setCourseId(usable[0]!.id);

        const examinerRoles = new Set(["super_admin", "admin", "curator", "mentor"]);
        const eligible = us.items.filter(
          (u) =>
            !u.deleted_at && u.roles.some((r) => examinerRoles.has(r)),
        );
        setExaminers(eligible);
      } catch (err) {
        setError(
          err instanceof ApiError ? `Ошибка ${err.status}` : "Сеть недоступна",
        );
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  async function handleSubmit() {
    if (typeof courseId !== "number" || !date || !time) {
      setError("Заполните курс, дату и время");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await scheduleExam({
        trainee_user_id: traineeUserId,
        course_id: courseId,
        examiner_user_id:
          typeof examinerId === "number" ? examinerId : null,
        scheduled_at: moscowDateTimeToUtcIso(`${date}T${time}`)!,
        format,
        comment: comment.trim() || null,
      });
      onScheduled();
    } catch (err) {
      setError(
        err instanceof ApiError ? `Ошибка ${err.status}` : "Не удалось",
      );
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-ink/50 p-4">
      <div className="card w-full max-w-lg p-5">
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-ink">Назначить экзамен</h2>
            <p className="mt-0.5 text-xs text-ink-muted">Ученик: {traineeName}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="grid h-8 w-8 place-items-center rounded-lg text-ink-soft transition hover:bg-surface-alt"
            aria-label="Закрыть"
          >
            <X size={16} strokeWidth={2} />
          </button>
        </div>

        {error && (
          <div className="mb-3 rounded bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}

        {loading ? (
          <div className="py-8 text-center text-sm text-ink-muted">Загрузка…</div>
        ) : (
          <div className="space-y-4">
            <label className="block">
              <span className="text-xs font-semibold uppercase tracking-wide text-ink-muted">
                Курс / маршрут
              </span>
              <select
                value={courseId}
                onChange={(e) => setCourseId(Number(e.target.value))}
                disabled={submitting}
                className="input mt-1.5 w-full"
              >
                {courses.length === 0 && <option value="">— нет курсов —</option>}
                {courses.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.title}
                    {c.status === "draft" ? " [черновик]" : ""}
                  </option>
                ))}
              </select>
            </label>

            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="text-xs font-semibold uppercase tracking-wide text-ink-muted">
                  Дата
                </span>
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  disabled={submitting}
                  className="input mt-1.5 w-full"
                />
              </label>
              <label className="block">
                <span className="text-xs font-semibold uppercase tracking-wide text-ink-muted">
                  Время
                </span>
                <input
                  type="time"
                  value={time}
                  onChange={(e) => setTime(e.target.value)}
                  disabled={submitting}
                  className="input mt-1.5 w-full"
                />
              </label>
            </div>

            <label className="block">
              <span className="text-xs font-semibold uppercase tracking-wide text-ink-muted">
                Куратор / экзаменатор
              </span>
              <select
                value={examinerId}
                onChange={(e) =>
                  setExaminerId(e.target.value ? Number(e.target.value) : "")
                }
                disabled={submitting}
                className="input mt-1.5 w-full"
              >
                <option value="">— не указан —</option>
                {examiners.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.full_name} ({u.login})
                  </option>
                ))}
              </select>
            </label>

            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-ink-muted">
                Формат
              </div>
              <div className="mt-1.5 flex gap-2">
                {(["in_person", "online"] as ExamFormat[]).map((f) => (
                  <button
                    key={f}
                    type="button"
                    onClick={() => setFormat(f)}
                    disabled={submitting}
                    className={`flex-1 rounded-lg border px-3 py-2 text-sm font-medium transition ${
                      format === f
                        ? "border-brand bg-brand/5 text-brand"
                        : "border-line bg-white text-ink-soft hover:border-ink-muted/40"
                    }`}
                  >
                    {f === "in_person" ? "Очный" : "Онлайн"}
                  </button>
                ))}
              </div>
            </div>

            <label className="block">
              <span className="text-xs font-semibold uppercase tracking-wide text-ink-muted">
                Комментарий (необязательно)
              </span>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                disabled={submitting}
                rows={2}
                className="input mt-1.5 w-full"
                placeholder="Что обсудить, на что обратить внимание…"
              />
            </label>

            <div className="flex items-center justify-end gap-2 pt-1">
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                className="btn-secondary"
              >
                Отмена
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={submitting || typeof courseId !== "number" || !date}
                className="btn-primary"
              >
                {submitting ? "Назначаем…" : "Назначить экзамен"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
